import { Directive, effect, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Directive to automatically translate the text content of a label element
 * and apply a default CSS class for styling. Used inside the <app-input-group>
 *
 * This directive listens for changes in the label's text content and updates
 * it with the translated value using the TranslateService. It also provides
 * methods to set attributes on the label element, such as associating the
 * label with a form control via the `for` attribute.
 *
 * ```html
 * <app-input-group>
 *   <label appLabel>Your label</label>
 *   <input type="text" appInput formControlName="yourValue">
 * </app-input-group>
 * ```
 */
@Directive({
  selector: 'label[appLabel]'
})
export class LabelDirective {

  /**
   * Creates an instance of LabelDirective.
   * @param elementRef Reference to the host label element.
   * @param translateService Service used to translate the label's text content.
   */
  constructor(
    private readonly elementRef: ElementRef,
    private readonly translateService: TranslateService
  ) {
    this.elementRef.nativeElement.classList.add('ec-input-label');

    effect(() => {
      const content = this.elementRef.nativeElement.textContent?.trim();

      if (content) {
        /** Define title based on value **/
        if (!this.elementRef.nativeElement.hasAttribute('title')) {
          this.setAttribute({ name: 'title', value: content });
        }
      }
    });
  }

  /**
   * Sets an attribute on the label element.
   * @param attribute Object containing the name and value of the attribute to set.
   */
  public setAttribute(attribute: { name: string, value: string }): void {
    if (this.elementRef.nativeElement) {
      this.elementRef.nativeElement.setAttribute(
        attribute.name,
        attribute.value
      );
    }
  }

  /**
   * Sets the 'for' attribute on the label element to associate it with a form control.
   * @param id The id of the form control that this label is associated with.
   */
  public setFor(id: string): void {
    this.setAttribute({ name: 'for', value: id });
  }

  public getLabel(): string {
    return this.elementRef.nativeElement.textContent?.trim() ?? '';
  }
}
